import { Popover, PopoverButton, PopoverPanel } from '@headlessui/react'
import { CalendarDaysIcon, ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline'
import dayjs from 'dayjs'
import { Datepicker } from 'headless-datetimepicker'
import PropTypes from 'prop-types'
import React from 'react'
import { twMerge as mergeClassNames } from 'tailwind-merge'

const ISO = 'YYYY-MM-DD'
const toDate = (value) => (value ? dayjs(value).toDate() : null)
const toIso = (date) => (date ? dayjs(date).format(ISO) : '')

// Compact human summary of the selected range for the trigger button.
const summarize = (from, to) => {
  if (from && to) return `${dayjs(from).format('MMM D')} – ${dayjs(to).format('MMM D, YYYY')}`
  if (from) return `From ${dayjs(from).format('MMM D, YYYY')}`
  if (to) return `Until ${dayjs(to).format('MMM D, YYYY')}`
  return 'Any'
}

/**
 *
 * DateRangePicker
 *
 * A single popover trigger that opens a calendar for picking an inclusive
 * start/end date range. Built on headless-datetimepicker (single-date), with
 * range state managed here: the first click sets the start, the second sets the
 * end (auto-ordered). Empty = no bound. `value` is `{ from, to }` as YYYY-MM-DD
 * strings; `onChange` receives the same shape.
 */
const DateRangePicker = ({ label, value, onChange }) => {
  const from = toDate(value.from)
  const to = toDate(value.to)
  const hasValue = Boolean(value.from || value.to)

  const selectDay = (date) => {
    // Start a fresh range when nothing is set or the range is already complete.
    if (!from || (from && to)) {
      onChange({ from: toIso(date), to: '' })
    } else if (dayjs(date).isBefore(from, 'day')) {
      onChange({ from: toIso(date), to: toIso(from) })
    } else {
      onChange({ from: toIso(from), to: toIso(date) })
    }
  }

  const isEndpoint = (date) =>
    (from && dayjs(date).isSame(from, 'day')) || (to && dayjs(date).isSame(to, 'day'))
  const isInRange = (date) =>
    from && to && dayjs(date).isAfter(from, 'day') && dayjs(date).isBefore(to, 'day')

  return (
    <div className="flex flex-col gap-1">
      <span className="text-charcoal-400 text-[10px] font-bold uppercase tracking-widest">
        {label}
      </span>
      <Popover className="relative">
        <PopoverButton
          className={mergeClassNames(
            'bg-background border-brownGray-200 hover:border-brownGray-300 flex w-full items-center justify-between gap-2 rounded-lg border-2 px-3 py-2 text-sm outline-none ring-0',
            hasValue && 'border-blue-800 hover:border-blue-900',
          )}
        >
          <span className={hasValue ? 'text-charcoal-900' : 'text-charcoal-400'}>
            {summarize(from, to)}
          </span>
          <CalendarDaysIcon
            className={mergeClassNames(
              'h-4 w-4',
              hasValue ? 'text-blue-800' : 'text-charcoal-400',
            )}
          />
        </PopoverButton>

        {/* In-place (not portaled) so it stays inside the parent filter popover
            and clicking it doesn't dismiss that popover. */}
        <PopoverPanel className="absolute left-0 z-30 mt-1 w-[300px] rounded-lg bg-white p-3 shadow-md">
          <Datepicker defaultValue={from || to || undefined}>
            <Datepicker.Picker defaultType="day" alwaysOpen className="w-full">
              {({ year, monthName }) => (
                <>
                  <div className="mb-2 flex items-center justify-between">
                    <Datepicker.Button
                      action="prev"
                      className="hover:bg-charcoal-light rounded p-1"
                      aria-label="Previous month"
                    >
                      <ChevronLeftIcon className="h-4 w-4 stroke-blue-800" />
                    </Datepicker.Button>
                    <span className="text-charcoal-900 text-sm font-semibold">
                      {monthName} {year}
                    </span>
                    <Datepicker.Button
                      action="next"
                      className="hover:bg-charcoal-light rounded p-1"
                      aria-label="Next month"
                    >
                      <ChevronRightIcon className="h-4 w-4 stroke-blue-800" />
                    </Datepicker.Button>
                  </div>

                  <Datepicker.Items type="day" className="grid grid-cols-7 gap-1">
                    {({ items }) =>
                      items.map((item) =>
                        item.isHeader ? (
                          <div
                            key={item.key}
                            className="text-charcoal-400 flex h-7 items-center justify-center text-[10px] font-bold uppercase"
                          >
                            {/* 2-letter labels (Su Mo Tu We Th Fr Sa) — unique
                                prefixes, so they never overlap in the narrow cells. */}
                            {item.text.slice(0, 2)}
                          </div>
                        ) : (
                          <Datepicker.Item
                            key={item.key}
                            item={item}
                            onClick={() => selectDay(item.value)}
                            className={mergeClassNames(
                              'flex h-8 cursor-pointer select-none items-center justify-center rounded text-sm hover:bg-blue-100',
                              !item.isInCurrentMonth && 'text-charcoal-light',
                              item.isInCurrentMonth && 'text-charcoal-900',
                              isInRange(item.value) && 'bg-blue-100',
                              isEndpoint(item.value) && 'bg-blue-800 text-white hover:bg-blue-900',
                            )}
                          >
                            {item.text}
                          </Datepicker.Item>
                        ),
                      )
                    }
                  </Datepicker.Items>
                </>
              )}
            </Datepicker.Picker>
          </Datepicker>

          <div className="mt-3 flex items-center justify-between border-t border-gray-100 pt-2">
            <span className="text-charcoal-400 text-xs">{summarize(from, to)}</span>
            {hasValue && (
              <button
                type="button"
                className="text-sm font-semibold text-blue-800 hover:text-blue-900"
                onClick={() => onChange({ from: '', to: '' })}
              >
                Clear
              </button>
            )}
          </div>
        </PopoverPanel>
      </Popover>
    </div>
  )
}

DateRangePicker.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.shape({
    from: PropTypes.string,
    to: PropTypes.string,
  }).isRequired,
  onChange: PropTypes.func.isRequired,
}

export default DateRangePicker
